import { VStack } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import InputWithAdd from "src/components/InputWithAdd";
import SubHeader from "src/components/SubHeader";
import MessagesCard from "./MessagesCard";
import {
  createIntention,
  createSubliminal,
  deleteIntention,
  deleteSubliminal,
} from "src/api/affirmation/intentions/mutations";
import { IntentionsSavedList } from "src/types";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getList,
  getListSubliminal,
  getSharedList,
} from "src/api/affirmation/intentions/query";
import { useIntl } from "react-intl";
import { INTENTION_SECTION_WIDTH } from "./utils";
import { useFreeTrialToast } from "src/store/hooks/useFreeTrialToast";
import FreeTrialLock from "src/components/FreeTrialLock/FreeTrialLock";
import { useFreeTrial } from "src/free-trial/helpers";

interface Props {
  onClick?: any;
  onBlur?: any;
  name?: string;
  subliminal?: any;
  share?: () => void;
  onIntentionAdded?: () => void;
}

const SpecialIntentions: React.FC<Props> = ({
  onClick,
  onBlur,
  subliminal,
  name,
  share,
  onIntentionAdded,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [added, setAdded] = useState<boolean>(false);
  const [deleteAction, setDeleteAction] = useState<boolean>(false);
  const client = useQueryClient();
  const intl = useIntl();
  const { showFreeTrialToast } = useFreeTrialToast();

  const deleteMessage = async (component?: string, id?: number) => {
    if (component === "sub") {
      await deleteSubliminal(id!);
      setDeleteAction(!deleteAction);
      return;
    }
    await deleteIntention(id!);
    setDeleteAction(!deleteAction);
  };

  let determineDataFunc = !subliminal ? getList : getListSubliminal;
  let determineSharedDataFunc = !subliminal ? getSharedList : getListSubliminal;

  const { data } = useQuery<IntentionsSavedList, Error, IntentionsSavedList>(
    ...determineDataFunc
  );

  const { hasPermission: shouldAddIntention } = useFreeTrial(data?.length || 0);

  const { data: sharedData, refetch } = useQuery<any>(
    ...determineSharedDataFunc
  );
  useEffect(() => {}, [subliminal]);

  const { mutateAsync: createIntentionMutation } = useMutation(
    createIntention,
    {
      onSuccess: () => client.invalidateQueries("listIntention"),
    }
  );
  const { mutateAsync: createSubliminalMutation } = useMutation(
    createSubliminal,
    {
      onSuccess: () => client.invalidateQueries("listSubliminal"),
    }
  );
  const createMessage = () => {
    // Check if user can add intentions (for free trial users)
    if (!shouldAddIntention) {
      return showFreeTrialToast({
        message: intl.formatMessage({
          id: "freeTrial.intentionsLimitReached",
        }),
      });
    }

    const createdMessage = { name: inputValue };
    subliminal
      ? createSubliminalMutation(createdMessage)
      : createIntentionMutation(createdMessage);

    setAdded(!added);
    setInputValue("");

    if (onIntentionAdded) {
      onIntentionAdded();
    }
  };

  React.useEffect(() => {
    name && setInputValue(name);
  }, [added, deleteAction, name]);

  function removeDuplicateIds(arr) {
    let uniqueIds = new Set();
    return arr.filter((obj) => {
      if (uniqueIds.has(obj.id)) {
        return false;
      } else {
        uniqueIds.add(obj.id);
        return true;
      }
    });
  }

  return (
    // @ts-ignore
    <VStack
      width={"100%"}
      maxWidth={INTENTION_SECTION_WIDTH}
      alignItems={"start"}
    >
      <SubHeader
        marginTop={".3rem"}
        formatId={subliminal ? "subliminal.special" : "intentions.special"}
      />
      <FreeTrialLock isLocked={!shouldAddIntention}>
        <InputWithAdd
          disabled={inputValue === ""}
          onKeyDown={(event: any) => {
            if (event.which === 13 || event.keyCode === 13) {
              createMessage();
            }
          }}
          id={"intentions"}
          value={inputValue}
          onClick={() => createMessage()}
          onChange={(e: any) => {
            setInputValue(e.target.value);
          }}
          marginBottom={"32px"}
          width={INTENTION_SECTION_WIDTH}
          placeholder={
            subliminal
              ? intl.formatMessage({ id: "subliminal.generalmessage" })
              : intl.formatMessage({ id: "intentions.mymessage" })
          }
        />
      </FreeTrialLock>
      {/* <button onClick={refetch}>refetc</button> */}
      {data && sharedData ? (
        <MessagesCard
          onDelete={deleteMessage}
          deleteAction={deleteAction}
          data={removeDuplicateIds([...data, ...sharedData])}
          addInput={added}
          subliminalSpecial={subliminal}
          onBlur={onBlur}
          onClick={onClick}
          remove={true}
          share={share}
        />
      ) : null}
    </VStack>
  );
};

export default SpecialIntentions;
